import { BreadcrumbData } from '@/components/breadcrumbs/breadcrumbs.props';
import { useLocation } from 'react-router-dom';

export const useGetRouteParam = (param: string) => {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  return params.get(param);
};
export const useFullPathParts = () => {
  const { pathname, search } = useLocation();
  const relativeUrl = pathname.startsWith('/') ? pathname.slice(1) : pathname;
  return { fullPath: relativeUrl + search, pathname: relativeUrl, params: search };
};

export interface UrlParams extends Record<string, string | undefined> {
  customerId?: string;
  projectId?: string;
  farmId?: string;
  fieldId?: string;
}

export interface ParsedIds {
  customerId: string;
  projectId: string;
  farmId: string;
  fieldId: string;
}

//Parses breadcrumb data to extract correct parent IDs for URL hierarchy
export const parseCorrectIds = (breadcrumbData?: BreadcrumbData): Partial<ParsedIds> => {
  if (!breadcrumbData) return {};

  return {
    customerId: breadcrumbData.client?.id,
    projectId: breadcrumbData.project?.id,
    farmId: breadcrumbData.farm?.id,
    fieldId: breadcrumbData.field?.id,
  };
};

// Short-circuit evaluation to check each level of hierarchy for mismatches
export const needsCorrection = (currentParams: UrlParams, correctIds: Partial<ParsedIds>): boolean => {
  if (correctIds.customerId && currentParams.customerId !== correctIds.customerId) return true;
  if (correctIds.projectId && currentParams.projectId !== correctIds.projectId) return true;
  if (correctIds.farmId && currentParams.farmId !== correctIds.farmId) return true;
  if (correctIds.fieldId && currentParams.fieldId !== correctIds.fieldId) return true;

  return false;
};

// Builds the correct URL path based on the provided IDs and current route type
export const buildCorrectUrl = (correctIds: Partial<ParsedIds>, currentPath: string): string => {
  const { customerId, projectId, farmId, fieldId } = correctIds;

  // Determine the route type based on current path
  if (currentPath.includes('/fields/') && fieldId && farmId && projectId && customerId) {
    return `/customers/${customerId}/projects/${projectId}/farms/${farmId}/fields/${fieldId}`;
  }

  if (currentPath.includes('/farms/') && farmId && projectId && customerId) {
    return `/customers/${customerId}/projects/${projectId}/farms/${farmId}`;
  }

  if (currentPath.includes('/projects/') && projectId && customerId) {
    return `/customers/${customerId}/projects/${projectId}`;
  }

  return currentPath; // Return unchanged can't build valid URL
};

export const validateAndGetCorrectUrl = (
  currentParams: UrlParams,
  breadcrumbData?: BreadcrumbData,
  currentPath?: string
): string | null => {
  if (!breadcrumbData) return null;

  const correctIds = parseCorrectIds(breadcrumbData);

  if (!needsCorrection(currentParams, correctIds)) return null;

  const pathToUse = currentPath || window.location.pathname;
  const correctUrl = buildCorrectUrl(correctIds, pathToUse);

  return correctUrl !== pathToUse ? correctUrl : null;
};
